import React, { memo, useContext, useMemo } from 'react';
import { QuoteContext } from '../../../../contexts/Quote';
import { Blok } from '../../../../interfaces/Blok.interface';
import { StoryblokNode } from '../../../../interfaces/StoryblokNode.interface';
import { StoryblokQuoteInformationItem } from '../../../../interfaces/StoryblokQuoteInformationItem.interface';
import { Theme } from '../../../../interfaces/enums/Theme.enum';
import { withBlokWrapper } from '../../../BlokWrapper/BlokWrapper';
import QuoteStageBlokHeadingBar from '../QuoteStageBlokHeadingBar/QuoteStageBlokHeadingBar';
import QuoteDetailInformationItem from './QuoteDetailInformationItem';

interface QuoteDetailsBlokProps extends Blok {
  heading: string;
  info_items_to_show: StoryblokNode<StoryblokQuoteInformationItem>[];
}

interface QuoteDetailsProps {
  blok: QuoteDetailsBlokProps;
}

const getValueByPath = (obj: any, path: any) => {
  // Split the path string into an array of keys
  const keys = path.split('/');
  // Start with the initial object
  let current = obj;

  for (const key of keys) {
    // Check if current is an object and key exists
    if (current !== null && typeof current === 'object' && key in current) {
      // Go one level deeper in the object
      current = current[key];
    } else {
      // If the value is null or undefined, or the key doesn't exist, return undefined
      return undefined;
    }
  }

  return current;
};

const QuoteDetails: React.FC<QuoteDetailsProps> = ({ blok }) => {
  const { heading, info_items_to_show } = blok;
  const quoteContext = useContext(QuoteContext);

  const infoItemsWithMappedValue = useMemo(() => {
    return info_items_to_show.map(
      (info_item: StoryblokNode<StoryblokQuoteInformationItem>) => {
        return {
          ...info_item,
          content: {
            ...info_item.content,
            value: getValueByPath(quoteContext, info_item.content.api_key),
          },
        };
      }
    );
  }, [info_items_to_show, quoteContext]);

  return (
    <div>
      <QuoteStageBlokHeadingBar heading={heading} />
      <ul className="p-10 space-y-6">
        {infoItemsWithMappedValue.map(
          (info_item: StoryblokNode<StoryblokQuoteInformationItem>) =>
            info_item.content.value && (
              <QuoteDetailInformationItem
                key={info_item.content._uid}
                {...info_item.content}
              />
            )
        )}
      </ul>
    </div>
  );
};

export default memo(withBlokWrapper(QuoteDetails, Theme.LIGHT));
